import { Stack, TextField } from '@mui/material';
import type { DraftBlock } from './pageSchemaModel';
import type { Catalog } from './useCatalog';
import { MultiPicker, StorePicker } from './Pickers';

/**
 * Per-block CONFIG forms (D-page-schema-editor-8a): one inline form per block type,
 * each mirroring its `data` shape from the contract. The three REQUIRED variants
 * (carousel / categoryStrip / banner) carry a variant selector that starts unset, so
 * an unconfigured variant block cannot be saved. The hero CTA (label + STRING link)
 * and the banner / pillCta LINK ({kind, ref}) render as the two distinct shapes.
 *
 * Enum fields use native selects so the control is a plain option list (clear,
 * accessible and deterministic). The config edits the loose draft `data`; the strict
 * contract is enforced on save.
 */

type Data = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const arr = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);
const linkOf = (v: unknown): { kind: string; ref: string } => {
  const l = (v ?? {}) as { kind?: string; ref?: string };
  return { kind: l.kind ?? 'store', ref: l.ref ?? '' };
};

const Text = ({
  label,
  value,
  onChange,
  testId,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testId?: string;
  multiline?: boolean;
}) => (
  <TextField
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    margin="dense"
    size="small"
    fullWidth
    multiline={multiline}
    inputProps={{ 'data-testid': testId }}
  />
);

const Select = ({
  label,
  value,
  onChange,
  options,
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  testId?: string;
}) => (
  <TextField
    select
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    margin="dense"
    size="small"
    fullWidth
    slotProps={{ select: { native: true } }}
    inputProps={{ 'data-testid': testId }}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </TextField>
);

const LinkFields = ({
  link,
  set,
  prefix,
}: {
  link: { kind: string; ref: string };
  set: (link: { kind: string; ref: string }) => void;
  prefix: string;
}) => (
  <Stack direction="row" spacing={1}>
    <Select
      label="Link kind"
      value={link.kind}
      onChange={(kind) => set({ ...link, kind })}
      testId={`${prefix}-link-kind`}
      options={[
        { value: 'store', label: 'Store' },
        { value: 'category', label: 'Category' },
        { value: 'page', label: 'Page' },
        { value: 'external', label: 'External' },
      ]}
    />
    <Text
      label="Link ref"
      value={link.ref}
      onChange={(ref) => set({ ...link, ref })}
      testId={`${prefix}-link-ref`}
    />
  </Stack>
);

export const BlockConfigForm = ({
  block,
  onChange,
  catalog,
}: {
  block: DraftBlock;
  onChange: (data: Data) => void;
  catalog: Catalog;
}) => {
  const d = block.data;
  const set = (key: string, value: unknown) => onChange({ ...d, [key]: value });

  switch (block.type) {
    case 'hero': {
      const cta = (d.cta ?? {}) as { label?: string; link?: string };
      const setCta = (label: string, link: string) =>
        set('cta', label || link ? { label, link } : undefined);
      return (
        <Stack>
          <Text
            label="Image"
            value={str(d.image)}
            onChange={(v) => set('image', v)}
            testId="config-hero-image"
          />
          <Text
            label="Title"
            value={str(d.title)}
            onChange={(v) => set('title', v)}
            testId="config-hero-title"
          />
          <Text
            label="Subtitle"
            value={str(d.subtitle)}
            onChange={(v) => set('subtitle', v || undefined)}
          />
          <Text
            label="CTA label"
            value={cta.label ?? ''}
            onChange={(v) => setCta(v, cta.link ?? '')}
            testId="config-hero-cta-label"
          />
          <Text
            label="CTA link (url or page)"
            value={cta.link ?? ''}
            onChange={(v) => setCta(cta.label ?? '', v)}
            testId="config-hero-cta-link"
          />
        </Stack>
      );
    }
    case 'carousel':
      return (
        <Stack>
          <Select
            label="Variant"
            value={str(d.variant)}
            onChange={(v) => set('variant', v)}
            testId="config-carousel-variant"
            options={[
              { value: '', label: 'Choose a variant' },
              { value: 'store', label: 'Store' },
              { value: 'category', label: 'Category' },
              { value: 'promo', label: 'Promo' },
              { value: 'seasonal', label: 'Seasonal' },
            ]}
          />
          <Text
            label="Title"
            value={str(d.title)}
            onChange={(v) => set('title', v || undefined)}
          />
          <MultiPicker
            label="Stores"
            options={catalog.stores}
            value={arr(d.storeIds)}
            onChange={(v) => set('storeIds', v.length ? v : undefined)}
            testId="config-carousel-stores"
          />
          <MultiPicker
            label="Categories"
            options={catalog.categories}
            value={arr(d.categoryIds)}
            onChange={(v) => set('categoryIds', v.length ? v : undefined)}
            testId="config-carousel-categories"
          />
        </Stack>
      );
    case 'storeGrid':
      return (
        <Stack>
          <Text
            label="Title"
            value={str(d.title)}
            onChange={(v) => set('title', v || undefined)}
          />
          <MultiPicker
            label="Stores (at least one)"
            options={catalog.stores}
            value={arr(d.storeIds)}
            onChange={(v) => set('storeIds', v)}
            testId="config-storegrid-stores"
          />
          <Select
            label="Columns"
            value={String(d.columns ?? 2)}
            onChange={(v) => set('columns', Number(v))}
            testId="config-storegrid-columns"
            options={[
              { value: '2', label: '2' },
              { value: '3', label: '3' },
            ]}
          />
        </Stack>
      );
    case 'categoryStrip':
      return (
        <Stack>
          <Select
            label="Variant"
            value={str(d.variant)}
            onChange={(v) => set('variant', v)}
            testId="config-categorystrip-variant"
            options={[
              { value: '', label: 'Choose a variant' },
              { value: 'standard', label: 'Standard' },
              { value: 'row', label: 'Row' },
            ]}
          />
          <MultiPicker
            label="Categories (at least one)"
            options={catalog.categories}
            value={arr(d.categoryIds)}
            onChange={(v) => set('categoryIds', v)}
            testId="config-categorystrip-categories"
          />
        </Stack>
      );
    case 'banner':
      return (
        <Stack>
          <Select
            label="Variant"
            value={str(d.variant)}
            onChange={(v) => set('variant', v)}
            testId="config-banner-variant"
            options={[
              { value: '', label: 'Choose a variant' },
              { value: 'standard', label: 'Standard' },
              { value: 'promo', label: 'Promo' },
            ]}
          />
          <Text
            label="Image"
            value={str(d.image)}
            onChange={(v) => set('image', v)}
            testId="config-banner-image"
          />
          <LinkFields
            link={linkOf(d.link)}
            set={(link) => set('link', link)}
            prefix="config-banner"
          />
          <Text
            label="Aspect ratio"
            value={str(d.aspectRatio)}
            onChange={(v) => set('aspectRatio', v || undefined)}
          />
        </Stack>
      );
    case 'featuredStore':
      return (
        <Stack>
          <StorePicker
            label="Store"
            options={catalog.stores}
            value={str(d.storeId)}
            onChange={(v) => set('storeId', v)}
            testId="config-featuredstore-store"
          />
          <Text
            label="Headline"
            value={str(d.headline)}
            onChange={(v) => set('headline', v || undefined)}
          />
        </Stack>
      );
    case 'textBlock':
      return (
        <Stack>
          <Text
            label="Title"
            value={str(d.title)}
            onChange={(v) => set('title', v || undefined)}
          />
          <Text
            label="Body"
            value={str(d.body)}
            onChange={(v) => set('body', v)}
            testId="config-text-body"
            multiline
          />
          <Select
            label="Align"
            value={str(d.align)}
            onChange={(v) => set('align', v || undefined)}
            options={[
              { value: '', label: 'Default' },
              { value: 'left', label: 'Left' },
              { value: 'center', label: 'Center' },
            ]}
          />
        </Stack>
      );
    case 'divider':
      return (
        <Select
          label="Style"
          value={str(d.style) || 'thin'}
          onChange={(v) => set('style', v)}
          testId="config-divider-style"
          options={[
            { value: 'thin', label: 'Thin' },
            { value: 'thick', label: 'Thick' },
            { value: 'space', label: 'Space' },
          ]}
        />
      );
    case 'pillCta':
      return (
        <Stack>
          <Text
            label="Label"
            value={str(d.label)}
            onChange={(v) => set('label', v)}
            testId="config-pillcta-label"
          />
          <LinkFields
            link={linkOf(d.link)}
            set={(link) => set('link', link)}
            prefix="config-pillcta"
          />
          <Text
            label="Icon"
            value={str(d.icon)}
            onChange={(v) => set('icon', v || undefined)}
            testId="config-pillcta-icon"
          />
          <Select
            label="Tone"
            value={str(d.tone)}
            onChange={(v) => set('tone', v || undefined)}
            testId="config-pillcta-tone"
            options={[
              { value: '', label: 'Default' },
              { value: 'primary', label: 'Primary' },
              { value: 'secondary', label: 'Secondary' },
              { value: 'subtle', label: 'Subtle' },
            ]}
          />
        </Stack>
      );
  }
};
